import { Box, Stack, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { MOTION } from "../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { PBJ_ALPHA } from "./colors";
import { RecommendationAddControl } from "./RecommendationAddControl";
import { RecommendationImpactSummary } from "./RecommendationImpactSummary";
import { RecommendationRowHeader } from "./RecommendationRowHeader";
import { RecommendationRowNotes } from "./RecommendationRowNotes";
import { RecommendationSliderControl } from "./RecommendationSliderControl";
import type { PeriodMode } from "./RecommendationsSidebar";
import { buildPlanItem, calculateRecData, rescoreRecAgainstPlan } from "./recMath.utils";
import { cutFloorState } from "./recommendationRow.utils";
import type { PlanItem, RecommendationType } from "./recommendations.constants";
import { isRnLever } from "./signedCopy.utils";

// The rail reserved on every card (transparent when off) so the in-plan / suggested accent never
// shifts the card content sideways.
const ACCENT_RAIL_PX = 3;

type Props = {
	rec: RecommendationType;
	// Assumed hourly wage for this lever's role (drives the slider's HPPD↔spend math).
	wage: number;
	// Theme-derived categorical accent for this lever (resolved by the parent via recColor).
	color: string;
	// The plan item when this rec is in the Action Plan; undefined otherwise.
	selectedItem: PlanItem | undefined;
	// Live slider spend (plan value when selected, locally-tracked spendMap value otherwise).
	currentSpend: number;
	// Latest Suggest positioned this rec with a non-zero addition and it's not yet added — drives the
	// "Suggested" badge, the faint wash + accent rail, and the one-shot slider land-pulse.
	isSuggested: boolean;
	// No spend dialed in and not in the plan — the slider reads as "nothing requested".
	isInert: boolean;
	// Bumped by every Suggest / Reset — remounts the slider so its land pulse replays (see the slider control).
	suggestNonce: number;
	prediction: PbjPrediction;
	census: number;
	// The current Action Plan — the prefix the cumulative re-score attributes points against.
	selectedPlan: PlanItem[];
	periodMode: PeriodMode;
	// Day denominators for the math tooltip, resolved for the active period mode by the parent.
	periodDays: number;
	periodWeekendDays: number;
	// The live config draft — read only to tell the wage tooltip override vs. computed.
	draft: PbjModelUpsertBody;
	onAdd: (item: PlanItem) => void;
	onSlider: (rec: RecommendationType, newSpend: number) => void;
	// Opens the wage-overrides dialog from the control column's wage ⓘ.
	onEditWages: () => void;
};

/**
 * A single recommendation lever card in the "How" sidebar: icon + title (with a math tooltip), the
 * two-direction slider (`RecommendationSliderControl`), and the projected HPPD / dollars / points
 * (`RecommendationImpactSummary`) with an Add (or "Active") affordance, plus the RN-floor warning and
 * the cut-limit note under it. The points are the lever's marginal points over the plan prefix before
 * it (`rescoreRecAgainstPlan`), so the card always equals its Action Plan row.
 */
export const RecommendationRow: FC<Props> = ({
	rec,
	wage,
	color,
	selectedItem,
	currentSpend,
	isSuggested,
	isInert,
	suggestNonce,
	prediction,
	census,
	selectedPlan,
	periodMode,
	periodDays,
	periodWeekendDays,
	draft,
	onAdd,
	onSlider,
	onEditWages,
}) => {
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const brandBorderC = alpha(accent, PBJ_ALPHA.BORDER);
	const brandFaintC = alpha(accent, PBJ_ALPHA.FAINT);

	const isSelected = !!selectedItem;
	const isCut = currentSpend < 0;
	// When the rec sits in the plan, the slider and labels reflect the plan item's stored values
	// verbatim (BE-suggested or slider-adjusted). Otherwise project via FE math from the current spend.
	const feData = calculateRecData(rec, currentSpend, wage, prediction, census);
	// Per-category HPPD gaps are baseline-INDEPENDENT (hours/census/days/scale only), so the plan item's
	// stored breakdown and the fresh FE projection are interchangeable inputs to the re-score.
	const rescored = rescoreRecAgainstPlan(rec, selectedItem?.breakdown ?? feData.breakdown, selectedPlan, prediction, isSelected);
	const hours = selectedItem?.hours ?? feData.hours;
	const pricedSpend = selectedItem?.pricedSpend ?? feData.pricedSpend;
	const dollars = pricedSpend * census * (rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays);
	// A cut under one whole hour a day rounds to no change, so there's nothing to add. An add keeps the
	// Add button live at any value, as it always has.
	const isUncommittableCut = isCut && hours === 0;
	const showRnWarning = isRnLever(rec.impactedIds) && isCut;
	const cutFloor = cutFloorState(rec, prediction, wage, census, currentSpend);

	const handleAdd = (): void => {
		onAdd(
			buildPlanItem(rec, currentSpend, {
				impact: selectedItem?.impact ?? feData.impact,
				points: rescored.points,
				hours,
				hoursCatchup: selectedItem ? selectedItem.hoursCatchup : feData.hoursCatchup,
				pricedSpend,
				breakdown: rescored.breakdown,
			}),
		);
	};

	return (
		<Box
			sx={{
				p: 2,
				// An in-plan row is marked by a solid accent rail, not a full-row wash: a brand-tint fill
				// here is the same tone as the panel header + page canvas, so with most levers active the
				// panel flattened into one block. Only the transient suggested state carries a wash.
				bgcolor: isSuggested ? brandFaintC : "transparent",
				borderLeft: `${ACCENT_RAIL_PX}px solid`,
				borderLeftColor: isSelected ? accent : isSuggested ? brandBorderC : "transparent",
				transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}}
			data-testid={`pbj-model-recommendation-${rec.id}`}
		>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
				<RecommendationRowHeader
					rec={rec}
					color={color}
					isSuggested={isSuggested}
					census={census}
					periodMode={periodMode}
					periodDays={periodDays}
					periodWeekendDays={periodWeekendDays}
				/>
				<RecommendationSliderControl
					rec={rec}
					wage={wage}
					color={color}
					currentSpend={currentSpend}
					isSuggested={isSuggested}
					isInert={isInert}
					suggestNonce={suggestNonce}
					prediction={prediction}
					census={census}
					periodMode={periodMode}
					draft={draft}
					selectedPlan={selectedPlan}
					isSelected={isSelected}
					onChange={(next) => onSlider(rec, next)}
					onEditWages={onEditWages}
				/>
				{/* Sized to its content and never shrunk: the figures stay one line each and the slider
				    column gives way instead. */}
				<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between", flex: "0 0 auto" }}>
					<RecommendationImpactSummary
						recId={rec.id}
						color={color}
						impact={selectedItem?.impact ?? feData.impact}
						dollars={dollars}
						points={rescored.points}
						breakdown={rescored.breakdown}
						isCut={isCut}
					/>
					<RecommendationAddControl recId={rec.id} color={color} isSelected={isSelected} disabled={isUncommittableCut} onAdd={handleAdd} />
				</Stack>
			</Stack>
			<RecommendationRowNotes
				recId={rec.id}
				isUncommittableCut={!isSelected && isUncommittableCut}
				maxCutHoursPerDay={cutFloor.maxCutHoursPerDay}
				atCutFloor={cutFloor.atCutFloor}
				cutLimitKey={cutFloor.copyKey}
				showRnWarning={showRnWarning}
			/>
		</Box>
	);
};
